import { StrictMode, useSyncExternalStore } from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './App'
import { AuthProvider } from './auth'
import { configureBleAdapter, installNativeBleAdapter } from './ble'
import { API_BASE_URL } from './auth/config'
import { nativeApiTransport } from './auth/nativeHttp'
import { fetchTransport, type ApiTransport } from './auth/transport'
import { installUnhandledRejectionLogging } from './components/ErrorBoundary'
import {
  HomeNoticePortal,
  installClientConfigRefresh,
  isBuildBelowFloor,
  subscribeToClientConfig,
} from './config'
import { BlockedScreen } from './screens/blocked/BlockedScreen'
import { installScrollToTopBridge } from './components/shellScroller'
import { DeviceCacheProvider } from './device-cache'
import { I18nProvider } from './i18n/I18nProvider'
import { readLocaleMirror } from './i18n/localeMirror'
import { DEFAULT_LOCALE } from './i18n/locales'
import { installShellUpdateCheck } from './pwa/shellUpdate'
import { installStoreUpdateCheck } from './pwa/storeUpdate'
import { requireAuth, Router } from './router'
import { ThemeProvider } from './theme/ThemeProvider'
import { installColorScheme } from './theme/colorScheme'
import { installPortraitLock } from './theme/portraitLock'
import './theme/global.css'

/**
 * With no API configured, a development build talks to `dev/devTransport` instead of the
 * network — the *real* auth provider, storage, boot sequence, and device cache, against a
 * simulated far end. The `import()` is dynamic and guarded, so a production bundle contains
 * no trace of it; setting `VITE_SELOGATE_API_URL` bypasses it in development too.
 *
 * **D166 — `nativeApiTransport` wraps the real one, and only the real one.** On iOS in the P18
 * container it routes the request through Capacitor's native HTTP client, because API Gateway
 * refuses `capacitor://` in `allowOrigins` at all and no CORS answer exists; everywhere else it
 * delegates unchanged. It deliberately does **not** wrap `devTransport`: a development build with
 * no API configured must keep answering from the fake far end, and a fake far end proves nothing
 * about this path either way (`auth/nativeHttp.ts` carries the whole of the reasoning).
 */
const transport: ApiTransport =
  import.meta.env.DEV && API_BASE_URL === ''
    ? (await import('./dev/devTransport')).devTransport
    : nativeApiTransport(fetchTransport)

const root = document.getElementById('root')
if (root === null) throw new Error('#root not found')

// **D188 — the appearance preference.** Outside React for the same three reasons the four calls
// below it are, and FIRST among them because it is the one that decides what the app looks like.
//
// It is not the whole mechanism: `index.html`'s inline script has already stamped `data-scheme`,
// before this bundle was fetched, because a deferred module can paint a cream shell at a resident
// who chose graphite. What this adds is the half that needs a live page — syncing both
// `theme-color` tags to the resolved surface, and keeping `auto` following the platform when the
// OS crosses sunset with the app already open. Never torn down: there is one document.
installColorScheme()

// Outside React on purpose: it is a property of the document, it must be right on the first
// paint after a rotation, and it has no state a component would own. Never torn down.
installPortraitLock()

// Also outside React, and for the same reason: it listens to the document rather than to any
// screen, and it must be listening before the first resume. `registerSW.js` registers the service
// worker on `load` and never asks again — a standalone app resumed from the app switcher fires no
// `load`, so without this a resident can hold an old shell indefinitely. It asks; it does not
// reload (see `pwa/shellUpdate.ts`). Never torn down.
installShellUpdateCheck()

// **D266's store rail, and the container is the only place it does anything.**
//
// Called unconditionally and outside React for the reason above it: it listens to the document
// rather than to any screen, and it must be listening before the first resume. In a browser
// `installStoreUpdateCheck()` returns before registering anything at all — its bridge predicate
// fails on `isNativeContainer()` — so the cost here is one function call. In the container it is
// the *only* thing that ever says a newer binary exists: D161(e) strips the service worker, so
// `installShellUpdateCheck()` above finds nothing to watch and the sidebar's row would otherwise
// never appear. Never torn down.
installStoreUpdateCheck()

// R1-5's telemetry seam, and outside React for the same reason as the two calls above it: it has
// to be listening before the first render, and it listens to the document rather than to any
// screen. It logs and does not swallow — see `components/ErrorBoundary.tsx`. Never torn down.
installUnhandledRejectionLogging()

// **P20 — the native radio, and this is the only production call to `configureBleAdapter`.**
//
// Outside React and before the first render, because `bleAdapter().supported()` is read during
// Gate Detail's own render to decide whether a second transport exists at all: installed later, the
// first paint after a cold launch would say the gate has no BLE and then change its mind.
//
// In a browser this does nothing and P16's Web Bluetooth radio stays installed. Inside P18's
// container it swaps in the Capacitor leaf on **both** platforms — WKWebView has no
// `navigator.bluetooth`, and neither does the Android System WebView, so wrapping the PWA removed
// BLE from the one platform that had it (`ble/nativeBluetooth.ts` carries the whole of the
// reasoning). Never torn down: there is exactly one radio.
installNativeBleAdapter(configureBleAdapter)

// **D178 — the iOS status-bar tap, and the fourth listener installed outside React for the same
// reason as the three above: it belongs to the window rather than to any screen, and it has to be
// listening before the first render because the tap can land on the first painted frame.
//
// iOS routes a status-bar tap to the `UIScrollView` carrying `scrollsToTop`. D132 took the scroll
// off the document, so the only scroller in the app is a CSS `overflow-y` div that UIKit cannot
// see, and the tap has resolved to a scroll view already at offset zero on every surface since.
// The container's view controller intercepts it and dispatches an event this answers. Inert in a
// browser and on Android, where nothing dispatches it. Never torn down.
installScrollToTopBridge()

/**
 * **R9 §4.4 (D330) — the build floor, read ONCE, before any screen and before any provider.**
 *
 * `isBuildBelowFloor()` answers from the document the config store is already running on — the
 * live fetch has not happened yet at this point, so this is the `localStorage` cache or the
 * compiled-in defaults (D328's second and third sources). **It is read exactly once per document
 * load**, here: a floor raised by the resume fetch takes effect at the next launch, and a floor
 * that arrives mid-session does not tear down a screen under the resident's hand. The release
 * direction is the one exception — `FloorGate` below.
 *
 * **The branch is the whole enforcement, and what it leaves out is the point.** Below the floor
 * there is no `AuthProvider`, no `DeviceCacheProvider` and no `Router` — the blocked screen reads
 * no session, no gate list and no API, so it renders identically offline and cannot itself be the
 * thing that fails. A gate inside `App` would have put a blocked resident behind a boot that can
 * hang, with no route to the update.
 *
 * **Every `install*()` above runs in both branches, including the store-update check**, which is
 * what makes the screen's one action live: `useAppUpdate` reads a rail that `installStoreUpdateCheck()`
 * has to have started. Nothing here is gated on the config fetch (§5).
 *
 * Hoisted out of the JSX deliberately: `StrictMode` double-invokes a render, and *once per
 * document load* is a claim about the read rather than about the memoized answer it would get.
 */
const blockedByFloor = isBuildBelowFloor()

/**
 * **D333 — below the floor, the one surface speaks the account's language, read from its mirror.**
 *
 * `locale` is cloud-owned and reconciled by `AuthProvider`, which the blocked arm deliberately
 * never mounts, so this is the only language the blocked screen can know: the device-local mirror
 * `AuthProvider` keeps of the account's value (`i18n/localeMirror.ts`), read synchronously here
 * beside the floor read and for the same reason — it must not wait on anything that can hang.
 *
 * **Only the blocked arm reads it, and that scope is D333 (e)'s, not an economy.** An unblocked
 * launch still starts at `DEFAULT_LOCALE` and is reconciled by the session as before: the
 * pre-sign-in UI's locale is the OTP SMS language and a new account's stored `User.locale`, and a
 * mirror seeding every launch would let device-local state write a cloud-owned setting. 🔸 The one
 * place the two meet: a device blocked at boot and then released live (`FloorGate` below) mounts the
 * app under the mirrored locale, which the session reconciles on sign-in exactly as it reconciles
 * the default — a signed-out resident there signs in in the last language used on the handset.
 */
const bootLocale = blockedByFloor ? readLocaleMirror() : DEFAULT_LOCALE

/**
 * **One-way gate: a device blocked at boot is released LIVE when the floor drops below its build,
 * and never blocked live when it rises.** The boot read above still decides whether the app tree
 * is withheld; what this adds is the other direction. The resume fetch keeps running behind the
 * blocked screen, so an operator who retracts a mistaken floor reached the device's cache but not
 * its screen — and the screen's one button is a dead tap wherever the store offers nothing, so a
 * resident who never force-quits (most of iOS) stayed blocked on a floor nobody was holding.
 * Rendering the app once `isBuildBelowFloor()` turns false is safe in that direction only: it
 * mounts, it tears nothing down.
 */
function FloorGate({ children }: { readonly children: JSX.Element }): JSX.Element {
  const belowFloor = useSyncExternalStore(subscribeToClientConfig, isBuildBelowFloor, isBuildBelowFloor)
  const stillBlocked = blockedByFloor && belowFloor
  return stillBlocked ? <BlockedScreen /> : children
}

createRoot(root).render(
  <StrictMode>
    <ThemeProvider>
      <I18nProvider initialLocale={bootLocale}>
        <FloorGate>
          <>
            <AuthProvider transport={transport}>
              <DeviceCacheProvider>
                <Router guards={[requireAuth]}>
                  <App />
                </Router>
              </DeviceCacheProvider>
            </AuthProvider>
            {/*
              R9 §8 S5 (D331) — a sibling of `<App/>` in the SAME root, never a prop threaded
              through it: `screens/home/HomeScreen.tsx` may not import `web/src/config/`, so this
              is mounted here instead and finds Home's slot through `components/homeNoticeHost.ts`'s
              registry, portaling into it with `createPortal` — which delivers real DOM events
              because it shares this root's fiber tree, exactly as `components/FrameOverlay.tsx`
              already relies on for the shell's own overlays. Needs only `I18nProvider`'s locale.

              **The fence R9 §5's prose does not describe.** §5 names "Home's OPEN CONTROL", but
              `infra/tests/client-config-client.test.ts` walks `screens/home` as a whole DIRECTORY,
              so `HomeScreen.tsx` cannot import this directory even transitively through the
              `components/` barrel it already imports whole. The portal is a forced consequence of
              that guard's scope rather than a preference, and the cost is durable: every future
              surface on `screens/home/`, `screens/gate/` or `ble/` that reads config pays the same
              indirection. The guard is deliberately NOT narrowed to match the prose — a transitive
              import from anywhere in Home reaches the open control, which is what §5 protects.

              **Inside the unblocked arm, and that placement is the merge's one decision.** S5 built
              it as a permanent sibling of the ternary, arguing its own `host === null` guard already
              renders nothing on the blocked path — true, and S5 confirmed the two placements are
              behaviourally identical. It sits here anyway, because D330's rule is that a build below
              the floor renders **one surface and nothing else**, and that reads better as a fact
              about the tree than as a fact about what a mounted component happens to return. Below
              the floor the config subscription, the dismissal store read and the host registry are
              never even reached, which is the same reasoning that put `BlockedScreen` outside
              `AuthProvider`: the blocked screen cannot itself be the thing that fails.
            */}
            <HomeNoticePortal />
          </>
        </FloorGate>
      </I18nProvider>
    </ThemeProvider>
  </StrictMode>,
)

// R9 §3.3 (D328) — the remote-config fetch is installed AFTER render and is never awaited: it
// runs behind a zero-delay timer, off the first paint's path, and a launch with no cache paints
// on the compiled-in defaults until it lands. Nothing on the open path reads it (R9 §5).
installClientConfigRefresh()
